// ============================================================================
//  csOpsExport.js  —  Excel export utility for CSOps dashboard
//
//  Lazy-loads SheetJS (xlsx) via dynamic import — matches the pattern in
//  src/components/ExportButtons.jsx so we don't bloat the main bundle.
// ============================================================================

/**
 * Generate and download an .xlsx file from a list of plain JS objects.
 *
 * @param {Object} args
 * @param {string} args.filename    Filename WITHOUT extension; .xlsx appended.
 * @param {string} args.sheetName   Sheet tab name (max 31 chars per Excel limit).
 * @param {Array<Object>} args.rows Array of objects. Each property → column.
 * @param {Array<{key:string,label:string,formatter?:Function}>} args.columns
 *                                  Column definitions in display order.
 *                                  `formatter(value, row)` optional per-cell.
 * @param {Array<Array<string|number>>} [args.summaryRows]
 *                                  Optional rows to place above the headers
 *                                  (e.g. "Generated at", "Total pallets", etc.)
 */
export async function exportPalletList({
  filename,
  sheetName = 'Pallets',
  rows = [],
  columns = [],
  summaryRows = [],
}) {
  const XLSX = await import('xlsx');

  const headerLabels = columns.map(c => c.label);
  const dataMatrix   = rows.map(row =>
    columns.map(c => {
      const v = row[c.key];
      if (c.formatter) return c.formatter(v, row);
      if (v === null || v === undefined) return '';
      return v;
    })
  );

  const aoa = [
    ...summaryRows,
    headerLabels,
    ...dataMatrix,
  ];

  const ws = XLSX.utils.aoa_to_sheet(aoa);

  // Auto column widths (clamp 8..40)
  ws['!cols'] = columns.map((c, ci) => {
    const headerLen = String(c.label).length;
    const sampleLen = Math.max(0, ...dataMatrix.slice(0, 100).map(r => String(r[ci] ?? '').length));
    return { wch: Math.min(40, Math.max(8, Math.max(headerLen, sampleLen) + 2)) };
  });

  // Freeze the header row
  const headerRowIdx = summaryRows.length;
  ws['!freeze'] = { xSplit: 0, ySplit: headerRowIdx + 1 };

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName.slice(0, 31));

  const safeName = (filename || 'export').replace(/[^A-Za-z0-9_.-]+/g, '_');
  XLSX.writeFile(wb, safeName + '.xlsx', { compression: true });
}

/**
 * Standard pallet column set used by the drilldown tooltip export AND by the
 * bin occupancy "full chamber" export. Centralized so both stay in sync.
 *
 * The 18 columns the user asked for, in the user's listed order.
 */
export const PALLET_EXPORT_COLUMNS = [
  { key: 'production_date', label: 'Production Date' },
  { key: 'shift',           label: 'Shift'           },
  { key: 'product',         label: 'Product'         },
  { key: 'grade',           label: 'Grade'           },
  { key: 'pack_style',      label: 'Pack Style'      },
  { key: 'pack_size',       label: 'Pack Size'       },
  { key: 'brand',           label: 'Brand'           },
  { key: 'nav_id',          label: 'NAV ID'          },
  { key: 'product_id',      label: 'Product ID'      },
  { key: 'lot_no',          label: 'Lot No'          },
  { key: 'day_code',        label: 'Day Code'        },
  { key: 'trace_id',        label: 'Trace ID'        },
  { key: 'cs',              label: 'Cold Store'      },
  { key: 'bin',             label: 'Bin Location'    },
  { key: 'pallet_id',       label: 'Pallet ID'       },
  { key: 'cartons',         label: 'No of Cartons'   },
  { key: 'kgs',             label: 'Wt (KG)'         },
  { key: 'lbs',             label: 'Wt (LB)'         },
];
